'use client';

import { FunctionComponent, useState, useRef } from 'react';
import { Alert, Button, Modal, Row, Col } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';
import XForm from '@xFrame4/components/common/XForm';
import FieldEditor, { FieldEditorType } from '@xFrame4/components/common/FieldEditor';
import SpecialOfferContactFormEntry from '@business/contact/SpecialOfferContactFormEntry';
import { useAppStateSelector } from '@redux/hooks';
import { SpecialOfferContactFormEntryStatus } from '@business/contact/generated/SpecialOfferContactFormEntry.generated';
import SpecialOffer from '@business/offers/SpecialOffer';
import AppTracker from '@components/tracking/AppTracker';

export interface SpecialOfferContactFormProps
{
    /** The special offer to contact about */
    specialOffer: SpecialOffer;
    /** The current language */
    language: string;
    /** Whether the modal is shown */
    show: boolean;
    /** Callback when the modal should be closed */
    onHide: () => void;
}

/**
 * Component: a contact form for special offer booking inquiries that opens in a modal.
 */
const SpecialOfferContactForm: FunctionComponent<SpecialOfferContactFormProps> = (props) =>
{
    const t = useTranslate();
    const formRef = useRef<any>(null);
    const appLanguage = useAppStateSelector(state => state.layout.language);

    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [checkInDate, setCheckInDate] = useState<string>('');
    const [checkOutDate, setCheckOutDate] = useState<string>('');
    const [numberOfAdults, setNumberOfAdults] = useState<number>(2);
    const [numberOfChildren, setNumberOfChildren] = useState<number>(0);
    const [childrenAges, setChildrenAges] = useState<number[]>([]);
    const [numberOfRooms, setNumberOfRooms] = useState<number>(1);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    /** Update children ages array when number of children changes */
    const handleNumberOfChildrenChange = (value: number) =>
    {
        setNumberOfChildren(value);
        
        // Adjust childrenAges array to match the new number of children
        const newChildrenAges = [...childrenAges];
        if (value > childrenAges.length)
        {
            // Add new entries with default age -1 (not selected)
            for (let i = childrenAges.length; i < value; i++)
            {
                newChildrenAges.push(-1);
            }
        }
        else
        {
            // Remove extra entries
            newChildrenAges.splice(value);
        }
        setChildrenAges(newChildrenAges);
    }

    /** Update a specific child's age */
    const handleChildAgeChange = (index: number, age: number) =>
    {
        const newChildrenAges = [...childrenAges];
        newChildrenAges[index] = age;
        setChildrenAges(newChildrenAges);
    }

    /** Submit the form. */
    const submitForm = () =>
    {
        if (formRef.current != null) (formRef.current as any).submit();
    }

    /** Validate the form. */
    const validateForm = () =>
    {
        let errors: string[] = [];

        if (!name.trim())
        {
            errors.push(t('NAME_IS_REQUIRED'));
        }

        if (!email.trim())
        {
            errors.push(t('EMAIL_IS_REQUIRED'));
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        {
            errors.push(t('EMAIL_IS_INVALID'));
        }

        if (!phone.trim())
        {
            errors.push(t('PHONE_IS_REQUIRED'));
        }

        if (!checkInDate)
        {
            errors.push(t('CHECK_IN_DATE_IS_REQUIRED'));
        }

        if (!checkOutDate)
        {
            errors.push(t('CHECK_OUT_DATE_IS_REQUIRED'));
        }

        if (checkInDate && checkOutDate)
        {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            if (checkOut <= checkIn)
            {
                errors.push(t('CHECK_OUT_DATE_MUST_BE_AFTER_CHECK_IN_DATE'));
            }
        }

        // Validate children ages if there are children
        if (numberOfChildren > 0)
        {
            const allAgesSelected = childrenAges.every(age => age >= 0 && age <= 17);
            if (!allAgesSelected)
            {
                errors.push(t('ALL_CHILDREN_AGES_MUST_BE_SELECTED'));
            }
        }

        setErrorMessages(errors);
        return errors.length === 0;
    }

    /** Save the contact message to the database. */
    const saveContactMessage = async () =>
    {
        setIsSubmitting(true);
        try
        {
            let contactFormEntry = new SpecialOfferContactFormEntry();
            contactFormEntry.specialOffer = props.specialOffer;
            contactFormEntry.name = name;
            contactFormEntry.email = email;
            contactFormEntry.phone = phone;
            contactFormEntry.message = message;
            contactFormEntry.checkInDate = checkInDate;
            contactFormEntry.checkOutDate = checkOutDate;
            contactFormEntry.numberOfAdults = numberOfAdults;
            contactFormEntry.numberOfChildren = numberOfChildren;
            contactFormEntry.childrenAges = numberOfChildren > 0 ? childrenAges : null;
            contactFormEntry.numberOfRooms = numberOfRooms;
            contactFormEntry.status = SpecialOfferContactFormEntryStatus.NEW;
            contactFormEntry.language = props.language;

            await contactFormEntry.save();

            // Track the special offer contact form submission
            AppTracker.trackSpecialOfferContactFormSubmit(email, phone);

            // Reset form
            setName('');
            setEmail('');
            setPhone('');
            setMessage('');
            setCheckInDate('');
            setCheckOutDate('');
            setNumberOfAdults(2);
            setNumberOfChildren(0);
            setChildrenAges([]);
            setNumberOfRooms(1);
            setSuccessMessage(t('SPECIAL_OFFER_CONTACT_FORM_SUCCESS_MESSAGE'));
            setErrorMessages([]);

            // Hide modal after 1 seconds
            setTimeout(() =>
            {
                props.onHide();
                setSuccessMessage(undefined);
            }, 1000);
        }
        catch (error)
        {
            setErrorMessages([t('CONTACT_FORM_ERROR_MESSAGE')]);
        }
        finally
        {
            setIsSubmitting(false);
        }
    }

    /** Render */
    return (
        <Modal 
            show={props.show} 
            onHide={props.onHide}
            size="lg"
            centered
            className="special-offer-contact-modal"
        >
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title>{t('BOOKING_INQUIRY')} - {props.specialOffer.getTranslation(props.language)?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <XForm
                    ref={formRef}
                    onValidate={() => validateForm()}
                    onSubmit={() => saveContactMessage()}
                >
                    <Row>
                        <Col>
                            <FieldEditor
                                type={FieldEditorType.Text}
                                field="name"
                                label={t('NAME')}
                                isRequired={true}
                                value={name}
                                onValueChanged={(_field, value) => setName(value)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FieldEditor
                                type={FieldEditorType.Email}
                                field="email"
                                label={t('EMAIL')}
                                isRequired={true}
                                value={email}
                                onValueChanged={(_field, value) => setEmail(value)}
                            />
                        </Col>
                        <Col md={6}>
                            <FieldEditor
                                type={FieldEditorType.Text}
                                field="phone"
                                label={t('PHONE')}
                                isRequired={true}
                                value={phone}
                                onValueChanged={(_field, value) => setPhone(value)}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FieldEditor
                                type={FieldEditorType.Date}
                                field="checkInDate"
                                label={t('CHECK_IN_DATE')}
                                isRequired={true}
                                value={checkInDate}
                                onValueChanged={(_field, value) => setCheckInDate(value)}
                            />
                        </Col>
                        <Col md={6}>
                            <FieldEditor
                                type={FieldEditorType.Date}
                                field="checkOutDate"
                                label={t('CHECK_OUT_DATE')}
                                isRequired={true}
                                value={checkOutDate}
                                onValueChanged={(_field, value) => setCheckOutDate(value)}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <FieldEditor
                                type={FieldEditorType.NumericStepper}
                                field="numberOfAdults"
                                label={t('NUMBER_OF_ADULTS')}
                                min={1}
                                max={10}
                                isRequired={true}
                                value={numberOfAdults}
                                onValueChanged={(_field, value) => setNumberOfAdults(value)}
                            />
                        </Col>
                        <Col md={4}>
                            <FieldEditor
                                type={FieldEditorType.NumericStepper}
                                field="numberOfChildren"
                                label={t('NUMBER_OF_CHILDREN')}
                                min={0}
                                max={10}
                                value={numberOfChildren}
                                onValueChanged={(_field, value) => handleNumberOfChildrenChange(value)}
                            />
                        </Col>
                        <Col md={4}>
                            <FieldEditor
                                type={FieldEditorType.NumericStepper}
                                field="numberOfRooms"
                                label={t('NUMBER_OF_ROOMS')}
                                min={1}
                                max={10}
                                isRequired={true}
                                value={numberOfRooms}
                                onValueChanged={(_field, value) => setNumberOfRooms(value)}
                            />
                        </Col>
                    </Row>

                    {/* Children age selectors */}
                    {numberOfChildren > 0 && (
                        <Row className="mb-3">
                            <Col>
                                <label className="form-label">{t('CHILDREN_AGES')}</label>
                                <small className="text-muted d-block mb-2">
                                    {t('CHILDREN_AGE_HELP_TEXT')}
                                </small>
                                <Row>
                                    {Array.from({ length: numberOfChildren }, (_, index) => (
                                        <Col key={index} xs={6} md={4} lg={3} className="mb-2">
                                            <select
                                                className="form-select"
                                                value={childrenAges[index] ?? -1}
                                                onChange={(e) => handleChildAgeChange(index, parseInt(e.target.value))}
                                            >
                                                <option value={-1}>{t('AGE_NEEDED')}</option>
                                                {Array.from({ length: 18 }, (_, age) => (
                                                    <option key={age} value={age}>
                                                        {age === 0 ? t('UNDER_1_YEAR') : age === 1 ? t('1_YEAR_OLD') : `${age} ${t('YEARS_OLD')}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    )}

                    <FieldEditor
                        type={FieldEditorType.Textarea}
                        field="message"
                        label={t('MESSAGE')}
                        placeholder={t('SPECIAL_REQUESTS_OR_COMMENTS')}
                        value={message}
                        onValueChanged={(_field, value) => setMessage(value)}
                    />

                    <div className="messages mt-3">
                        {errorMessages.length > 0 &&
                            <div className="error-messages">
                                {errorMessages.map((errorMessage, index) => (
                                    <Alert key={index} variant="danger">{errorMessage}</Alert>
                                ))}
                            </div>
                        }
                        {successMessage &&
                            <div className="success-message">
                                <Alert variant="success">{successMessage}</Alert>
                            </div>
                        }
                    </div>
                </XForm>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>
                    {t('CANCEL')}
                </Button>
                <Button
                    variant="primary"
                    onClick={submitForm}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? t('SENDING') : t('SEND_INQUIRY')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SpecialOfferContactForm;